import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { EnvelopeIcon, PhoneIcon } from 'src/components/Icons/icons';
import { useNotification } from 'src/components/Notification/useNotfication';
import { Section } from 'src/components/Section';
import container from 'src/container';
import { NextOfKin } from 'src/domain/next-of-kin';
import { UserThunks } from 'src/state/thunks';
import Input from 'src/components/FormComponents/Input';
import { LoginContactFields } from 'src/components/AccountAccess/LoginContactFields';

import { Button } from 'src/components/Buttons/Button';
import { useLogout } from 'src/view/hooks/useLogout';
import {
    IsOptional,
    IsString,
    IsNotEmpty,
    IsEmail,
    ValidateIf,
} from 'class-validator';
import { PeopleSVG } from 'src/assets/svg';
import Form from 'src/components/FormComponents/Form';
import { useTranslation } from 'react-i18next';
import i18n from 'src/i18n';

const {
    cradle: { munduClient },
} = container;

class EditNextOfKinSchema {
    @IsOptional()
    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    firstName?: string;

    @IsOptional()
    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    lastName?: string;

    @IsOptional()
    @ValidateIf(o => o.email?.length !== 0)
    @IsString()
    @IsEmail({}, { message: () => i18n.t('validation.invalidEmail') })
    email?: string;
}

export const NextofkinProfile = ({ user }: { user: NextOfKin }) => {
    const notify = useNotification();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const handleUpdate = async (data: EditNextOfKinSchema) => {
        try {
            await munduClient.updateNextOfKin(user.id, data);
            dispatch(UserThunks.refreshUser());
            notify(t('notifications.profileUpdated'));
        } catch (e) {
            if (e instanceof Error) notify(e.message, true);
        }
    };

    return (
        <div className="flex flex-col gap-8 max-w-3xl mx-auto">
            <Form
                className="bg-lightPeach text-woodyBrown"
                schema={EditNextOfKinSchema}
                onSubmit={handleUpdate}
                mode="onChange"
                defaultValues={{
                    firstName: user.profile.firstName,
                    lastName: user.profile.lastName,
                }}
            >
                {({
                    register,
                    formState: { isValid, errors, isDirty, isSubmitting },
                }) => (
                    <div className="grid grid-cols-2 gap-4 h-full">
                        <div className="col-span-full flex justify-center">
                            {/* TODO: Replace with Role icon when ready */}
                            <div className="rounded-full w-36 h-36 bg-starkWhite flex justify-center items-center mb-4">
                                <PeopleSVG className="w-[18px] h-[18px]" />
                            </div>
                        </div>
                        <Input
                            className="text-input"
                            type="text"
                            label={t('profile.labels.firstName')}
                            error={errors.firstName}
                            {...register('firstName')}
                        />
                        <Input
                            className="text-input"
                            type="text"
                            label={t('profile.labels.lastName')}
                            error={errors.lastName}
                            {...register('lastName')}
                        />
                        <LoginContactFields
                            userId={user.id}
                            readOnly={Boolean(user.supportMode)}
                        />

                        <div className="flex items-center gap-6">
                            <Input
                                value={t('common.save')}
                                className="primary-btn-small"
                                disabled={!isValid || isSubmitting || !isDirty}
                                type="submit"
                            />
                        </div>
                    </div>
                )}
            </Form>

            <Section title={t('profile.delete.title')}>
                <div>{t('profile.delete.description')}</div>
                <DeleteNextofkin user={user} />
            </Section>
        </div>
    );
};

const DeleteNextofkin = ({ user }: { user: NextOfKin }) => {
    const [confirmLastName, setConfirmLastName] = useState('');
    const [canDelete, setCanDelete] = useState(false);
    const notify = useNotification();
    const logout = useLogout();
    const { t } = useTranslation();

    const onDelete = async () => {
        munduClient
            .deleteNextOfKin(user.id)
            .then(() => logout())
            .catch((error: Error) => notify(error.message, true));
    };

    useEffect(() => {
        if (confirmLastName === user.profile.lastName) {
            setCanDelete(true);
        } else {
            setCanDelete(false);
        }
    }, [confirmLastName, user.profile.lastName]);

    return (
        <>
            <div className="self-start flex items-end gap-3 flex-wrap">
                <Input
                    className="text-input"
                    type="email"
                    onChange={e => setConfirmLastName(e.currentTarget.value)}
                    label={t('profile.delete.confirmLabel', {
                        value: user.profile.lastName,
                    })}
                    leadingIcon={EnvelopeIcon}
                />
                <Button
                    title={t('profile.delete.button')}
                    onClick={onDelete}
                    disabled={!canDelete}
                />
            </div>
        </>
    );
};
